import { DiffContent, Entity, Mutation, TrackingBuilder, uuid } from '../base';
import { Context } from '../base';
import {assertType} from "../base/utils";
import { CachingRef, IdentityRef, Ref } from './Ref';

class Counterparty implements Entity<Counterparty> {
    static STORE = "Counterparty";
    private _uuid : string;
    private _name : string;

    constructor(uuid : string, name :string) {
        assertType(name, "name", String);
        assertType(uuid, "uuid", String);

        this._uuid = uuid;
        this._name = name;
    }

    get name() { return this._name; }
    get uuid() { return this._uuid; }


    builder() : CounterpartyBuilder {
        return new CounterpartyBuilder()
                .trackingOff()
                .uuid(this._uuid)
                .name(this._name)
                .trackingOn();
    }

    toJson() : CounterpartyJSON {
        return {
            uuid: this._uuid,
            name: this._name,
        }
    }

    getRef() : Ref<Counterparty> {
        return CachingRef.of(Counterparty, this._uuid, this);
    }

    static identityRef = new IdentityRef(Counterparty);
    static identityValue = new Counterparty(Counterparty.identityRef.uuid, "");

    static identity() {
        return Counterparty.identityValue;
    }

    static fromJson(counterparty : CounterpartyJSON) {
        const id = (counterparty.uuid) ? counterparty.uuid : uuid();
        return new Counterparty(id, counterparty.name);
    }

    static fromUuid(uuid: string) : Counterparty | undefined {
        const finder = Context.getInstance().getFinder(Counterparty);
        return finder.findById(uuid);
    }

    static createRef(uuid: string) : Ref<Counterparty> {
        return CachingRef.of(Counterparty, uuid);
    }

    static sort(counterparties:Array<Counterparty>) {
        return counterparties.sort((c1, c2) => c1.name.localeCompare(c2.name));
    }
}

class CounterpartyBuilder extends TrackingBuilder<Counterparty> {
    private _uuid: string;
    private _name: string;
    protected _diff:CounterpartyDiff = {};

    constructor() {
        super();
        const cont = Counterparty.identity();
        this._uuid = cont.uuid;
        this._name = cont.name;
    }

    uuid(uuid: string) {
        if(this.isTracking() && this._uuid !== uuid) {
            this._diff.uuid = uuid;
        }
        this._uuid = uuid;
        return this;
    }

    name(name: string) {
        if(this.isTracking() && this._name !== name) {
            this._diff.name = name;
        }
        this._name = name;
        return this;
    }

    build(): Counterparty {
        return new Counterparty(this._uuid, this._name);
    }

    applyDiff(diff: CounterpartyDiff): CounterpartyBuilder {
        if (diff.name) {
            this.name(diff.name);
        }
        if (diff.uuid) {
            this.uuid(diff.uuid);
        }
        return this;
    }

    applyObj(object: Counterparty): CounterpartyBuilder {
        this.uuid(object.uuid);
        this.name(object.name);
        return this;
    }
    
    getDiff(): CounterpartyDiff {
        return this._diff;
    }
}

interface CounterpartyJSON {
    uuid: string,
    name: string,
}

interface CounterpartyDiff extends DiffContent<Counterparty> {
    uuid?: string,
    name?: string
}

export { Counterparty, CounterpartyBuilder, CounterpartyJSON, CounterpartyDiff };